// `CSVDownload` — US-150.
//
// Wave D11 (May 2026): the export pipeline moved server-side. The button
// now hits `GET /api/v1/leads/export.csv`, propagating the same filters
// the table is rendering, and the server streams the CSV directly. We
// keep the client-side blob trick *only* to attach the response body to
// an anchor so the browser surfaces the download UI; the data is no
// longer materialised from `useLeads()` results.

import { useState } from 'react';
import { Download } from 'lucide-react';

import { useT } from '@barel/i18n';
import { Button, toast } from '@barel/ui';
import { useApiClient } from '@barel/api-client';
import { getClientBaseUrl, getClientFetch } from '@barel/api-client';
import type { LeadsListFilter } from '@barel/api-client/react';

import { runMutationWithToast } from '@/lib/mutationToast';

export interface CSVDownloadProps {
  /** Filter set used by the live table — forwarded to the server endpoint. */
  filter?: LeadsListFilter;
  /** Render flavour — `button` (default) or `text` (link-style). */
  variant?: 'button' | 'text';
  size?: 'sm' | 'md';
  /**
   * Hint count for the empty-state guard. The server endpoint always
   * returns a header row so we cannot tell empty-vs-error from the body
   * alone; the FE knows the live count and skips the round-trip when it
   * is zero.
   */
  count?: number;
}

export function CSVDownload({ filter, variant = 'button', size = 'sm', count }: CSVDownloadProps) {
  const t = useT('leads');
  const client = useApiClient();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (typeof count === 'number' && count === 0) {
      toast.warning({ title: t('toast.csvEmpty') });
      return;
    }
    setDownloading(true);
    await runMutationWithToast(
      async () => {
        const baseUrl = getClientBaseUrl(client);
        const params = new URLSearchParams();
        if (filter?.account && filter.account !== 'all') params.set('account', filter.account);
        if (filter?.business && filter.business !== 'all') params.set('business', filter.business);
        if (filter?.status && filter.status !== 'all') params.set('status', filter.status);
        if (filter?.q) params.set('q', filter.q);
        if (filter?.includeArchived) params.set('includeArchived', '1');
        const qs = params.toString();
        const url = `${baseUrl}/api/v1/leads/export.csv${qs ? `?${qs}` : ''}`;
        const wrappedFetch = getClientFetch(client);
        const response = await wrappedFetch(url, { method: 'GET' });
        if (!response.ok) {
          throw new Error(`csv_export_failed_${response.status}`);
        }
        const blob = await response.blob();
        const filename = parseFilename(response.headers.get('content-disposition'));
        triggerDownload(blob, filename);
      },
      {
        success: { title: t('toast.csvDownloaded') },
        error: { title: t('toast.csvError') },
      },
    );
    setDownloading(false);
  };

  return (
    <Button
      type="button"
      variant={variant === 'text' ? 'ghost' : 'secondary'}
      size={size}
      iconLeft={<Download size={14} aria-hidden="true" />}
      onClick={handleDownload}
      disabled={downloading}
      data-testid="csv-download"
    >
      {t('list.exportCsv')}
    </Button>
  );
}

function parseFilename(headerValue: string | null): string {
  const fallback = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
  if (!headerValue) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(headerValue);
  return match?.[1] ?? fallback;
}

function triggerDownload(blob: Blob, filename: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
